import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  ClipboardList,
  Brain,
  Zap,
  Tag as TagIcon,
  Users,
  Calendar,
  Clock,
  Flag,
  FileText,
  X as XIcon,
} from 'lucide-react';
import FormDrawer, { FormSection } from './FormDrawer';
import {
  useProjects,
  useUsers,
  useTaskStatuses,
  useSprints,
  useAIValidation,
} from '../hooks/api';

const emptyForm = {
  name: '',
  description: '',
  project_id: '',
  sprint_id: '',
  estimated_hours: '',
  priority_level: 'medium',
  tags: [],
  due_date: '',
  status: 'To Do',
  assigned_to: [],
  enable_manual_logging: null,
  github_integration: undefined,
};

const priorityPillClass = (level) => {
  switch (level) {
    case 'urgent':
      return 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-200 border-rose-200 dark:border-rose-800';
    case 'high':
      return 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-200 border-orange-200 dark:border-orange-800';
    case 'medium':
      return 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-200 border-yellow-200 dark:border-yellow-800';
    case 'low':
    default:
      return 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-200 border-green-200 dark:border-green-800';
  }
};

/**
 * TaskFormDrawer
 *
 * Self-contained drawer that replaces both the inline task modal in
 * Tasks.js and the standalone TaskCreationModal used by QuickAccess.
 *
 * Props:
 *   open             boolean
 *   onClose          () => void
 *   editingTask      task being edited, or null
 *   currentUser      the auth user
 *   onSubmit         async (taskData) => void — parent performs the mutation
 *   initialProjectId optional — pre-select a project (used by QuickAccess)
 */
const TaskFormDrawer = ({
  open,
  onClose,
  editingTask,
  currentUser,
  onSubmit,
  initialProjectId = '',
}) => {
  const [formData, setFormData] = useState(emptyForm);
  const [newTag, setNewTag] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [aiSuggestedHours, setAiSuggestedHours] = useState(null);
  const [projectMembers, setProjectMembers] = useState([]);

  // Data hooks — self-contained so all callers stay thin
  const { data: projectsData } = useProjects();
  const { data: usersData } = useUsers();
  const { data: taskStatusesData } = useTaskStatuses();
  const { data: sprintsData } = useSprints(formData.project_id);
  const aiValidationMutation = useAIValidation();

  const projects = projectsData?.results || [];
  const availableMembers = useMemo(
    () => (Array.isArray(usersData) ? usersData : usersData?.results || []),
    [usersData]
  );
  const taskStatuses = Array.isArray(taskStatusesData) ? taskStatusesData : [];
  const sprints = sprintsData?.results || [];

  // Initialise / reset form whenever the drawer opens
  useEffect(() => {
    if (!open) return;
    if (editingTask) {
      setFormData({
        name: editingTask.name || '',
        description: editingTask.description || '',
        project_id: editingTask.project_id || '',
        sprint_id: editingTask.sprint_id || '',
        estimated_hours: editingTask.estimated_hours || '',
        priority_level: editingTask.priority_level || 'medium',
        tags: editingTask.tags || [],
        due_date: editingTask.due_date
          ? format(new Date(editingTask.due_date), 'yyyy-MM-dd')
          : '',
        status: editingTask.status || 'To Do',
        assigned_to: Array.isArray(editingTask.assigned_to)
          ? editingTask.assigned_to
          : editingTask.assigned_to
          ? [editingTask.assigned_to]
          : [],
        enable_manual_logging: editingTask.enable_manual_logging ?? null,
        github_integration: editingTask.github_integration
          ? {
              enabled: true,
              repository: editingTask.github_integration.repository,
              branch_name: editingTask.github_integration.branch_name,
              branch_prefix: editingTask.github_integration.branch_prefix,
            }
          : undefined,
      });
    } else {
      setFormData({ ...emptyForm, project_id: initialProjectId || '' });
    }
    setNewTag('');
    setAiSuggesting(false);
    setAiSuggestedHours(null);
  }, [open, editingTask, initialProjectId]);

  // Fetch project-specific members when project_id changes (admin / project_lead only)
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    if (
      formData.project_id &&
      (currentUser?.role === 'admin' || currentUser?.role === 'project_lead')
    ) {
      axios
        .get(`/api/projects/${formData.project_id}/details`)
        .then((res) => {
          if (cancelled) return;
          if (res.data && res.data.assigned_members) {
            setProjectMembers(res.data.assigned_members);
          } else {
            setProjectMembers([]);
          }
        })
        .catch(() => {
          if (!cancelled) setProjectMembers([]);
        });
    } else {
      setProjectMembers([]);
    }
    return () => {
      cancelled = true;
    };
  }, [open, formData.project_id, currentUser]);

  const handleAddTag = () => {
    const t = newTag.trim();
    if (t && !formData.tags.includes(t)) {
      setFormData((prev) => ({ ...prev, tags: [...prev.tags, t] }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleMemberToggle = (userId) => {
    setFormData((prev) => ({
      ...prev,
      assigned_to: prev.assigned_to.includes(userId)
        ? prev.assigned_to.filter((id) => id !== userId)
        : [...prev.assigned_to, userId],
    }));
  };

  const handleAiSuggest = async () => {
    setAiSuggesting(true);
    setAiSuggestedHours(null);
    try {
      const res = await aiValidationMutation.mutateAsync({
        title: formData.name,
        description: formData.description,
      });
      const data = res?.data || res;
      if (
        data?.suggested_hours_min !== undefined &&
        data?.suggested_hours_max !== undefined
      ) {
        setAiSuggestedHours({
          min: data.suggested_hours_min,
          max: data.suggested_hours_max,
          raw: data.raw_response,
        });
      } else if (data?.suggested_hours !== undefined) {
        setAiSuggestedHours({
          min: data.suggested_hours,
          max: data.suggested_hours,
          raw: data.raw_response,
        });
      } else {
        setAiSuggestedHours(null);
      }
    } catch (_) {
      toast.error('Failed to get AI suggestion');
    } finally {
      setAiSuggesting(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (isSubmitting) return;

    if (!formData.name || formData.name.trim() === '') {
      toast.error('Please enter a task name');
      return;
    }
    if (!formData.project_id) {
      toast.error('Please select a project');
      return;
    }
    if (!formData.description || formData.description.trim() === '') {
      toast.error('Please enter a task description');
      return;
    }
    if (!formData.due_date) {
      toast.error('Please select a due date');
      return;
    }
    if (!formData.estimated_hours) {
      toast.error('Please enter estimated hours');
      return;
    }
    if (parseFloat(formData.estimated_hours) < 0) {
      toast.error('Estimated hours cannot be negative');
      return;
    }
    if (!formData.assigned_to || formData.assigned_to.length === 0) {
      toast.error('At least one team member must be assigned to the task');
      return;
    }
    if (formData.github_integration?.enabled) {
      if (
        !formData.github_integration.repository ||
        !formData.github_integration.repository.includes('/')
      ) {
        toast.error(
          'Please enter a valid repository in format: username/repository-name'
        );
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const taskData = {
        ...formData,
        sprint_id: formData.sprint_id || null,
        estimated_hours: formData.estimated_hours
          ? parseFloat(formData.estimated_hours)
          : null,
      };

      if (taskData.github_integration?.enabled) {
        taskData.github_integration = {
          repository: taskData.github_integration.repository,
          branch_name: taskData.github_integration.branch_name || '',
          branch_prefix: taskData.github_integration.branch_prefix,
        };
      } else {
        delete taskData.github_integration;
      }

      await onSubmit(taskData);
      onClose?.();
    } catch (error) {
      const msg =
        error?.response?.data?.detail || error?.message || 'Failed to save task';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Assignee picker source: prefer project-specific members if we have them
  const assigneeSource =
    formData.project_id && projectMembers.length > 0 ? projectMembers : availableMembers;
  const assignableMembers = useMemo(
    () =>
      assigneeSource.filter((m) => {
        if (currentUser?.role === 'admin') {
          return !formData.assigned_to.includes(m.id);
        }
        return m.role !== 'admin' && !formData.assigned_to.includes(m.id);
      }),
    [assigneeSource, formData.assigned_to, currentUser]
  );

  const canManageAssignment =
    currentUser?.role === 'admin' || currentUser?.role === 'project_lead';

  const footer = (
    <>
      <button
        type="button"
        onClick={onClose}
        className="btn-secondary"
        disabled={isSubmitting}
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={handleSubmit}
        disabled={isSubmitting}
        className="btn-primary min-w-[140px] justify-center disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center gap-2">
            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            {editingTask ? 'Updating…' : 'Creating…'}
          </span>
        ) : editingTask ? (
          'Update Task'
        ) : (
          'Create Task'
        )}
      </button>
    </>
  );

  return (
    <FormDrawer
      open={open}
      onClose={onClose}
      title={
        formData.name
          ? formData.name
          : editingTask
          ? 'Edit Task'
          : 'Create New Task'
      }
      subtitle={
        editingTask
          ? 'Update the task details, planning, tags, and assignment.'
          : 'Define the work, estimate it, and assign the right people.'
      }
      icon={<ClipboardList className="h-5 w-5" />}
      iconColor="violet"
      footer={footer}
    >
      <form onSubmit={handleSubmit} className="space-y-0">
        <FormSection
          title="Basics"
          description="What is this task and which project does it belong to?"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">
                Task Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="input-field"
                placeholder="e.g. Implement login page"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">
                Project <span className="text-red-500">*</span>
              </label>
              <select
                className="input-field"
                value={formData.project_id}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    project_id: e.target.value,
                    sprint_id: '',
                  })
                }
                required
              >
                <option value="">Select a project</option>
                {Array.isArray(projects) &&
                  projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">
              <span className="inline-flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5" />
                Description <span className="text-red-500">*</span>
              </span>
            </label>
            <textarea
              className="input-field resize-none"
              rows="3"
              placeholder="What needs to be done? Add enough detail to guide the work."
              value={formData.description}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 250) {
                  setFormData({ ...formData, description: value });
                }
              }}
              maxLength={250}
              required
            />
            <div className="flex justify-between items-center mt-1">
              <span className="text-xs text-secondary-500 dark:text-secondary-400">
                <span className="font-semibold text-secondary-700 dark:text-secondary-300">
                  {formData.description ? formData.description.length : 0}
                </span>
                /250
              </span>
              <span className="text-xs text-secondary-500 dark:text-secondary-400">
                Max 250 characters
              </span>
            </div>
          </div>
        </FormSection>

        <FormSection
          title="Planning"
          description="Estimate, schedule, and prioritise the work."
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  Estimated Hours <span className="text-red-500">*</span>
                </span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  className="input-field flex-1"
                  placeholder="0"
                  value={formData.estimated_hours}
                  onChange={(e) =>
                    setFormData({ ...formData, estimated_hours: e.target.value })
                  }
                  required
                />
                <button
                  type="button"
                  className="p-2 rounded-lg bg-primary-50 hover:bg-primary-100 text-primary-600 border border-primary-200 dark:bg-primary-900/30 dark:border-primary-700 dark:text-primary-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Get AI Suggested Hours"
                  disabled={
                    aiSuggesting || !formData.name || !formData.description
                  }
                  onClick={handleAiSuggest}
                >
                  {aiSuggesting ? (
                    <span className="inline-block h-5 w-5 animate-spin rounded-full border-b-2 border-primary-500" />
                  ) : (
                    <Brain className="h-5 w-5" />
                  )}
                </button>
              </div>
              {aiSuggestedHours !== null && (
                <button
                  type="button"
                  className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 bg-primary-50 text-primary-700 rounded-full border border-primary-200 hover:bg-primary-100 transition dark:bg-primary-900/30 dark:text-primary-200 dark:border-primary-800"
                  title={
                    aiSuggestedHours.raw
                      ? `AI raw: ${aiSuggestedHours.raw}`
                      : 'Click to use this value'
                  }
                  onClick={() => {
                    const avg = (
                      (aiSuggestedHours.min + aiSuggestedHours.max) /
                      2
                    ).toFixed(1);
                    setFormData({ ...formData, estimated_hours: avg });
                  }}
                >
                  <Zap className="h-3.5 w-3.5" />
                  <span className="text-xs font-medium">
                    {aiSuggestedHours.min !== aiSuggestedHours.max
                      ? `${aiSuggestedHours.min}–${aiSuggestedHours.max}h`
                      : `${aiSuggestedHours.min}h`}
                  </span>
                </button>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">
                <span className="inline-flex items-center gap-1.5">
                  <Flag className="h-3.5 w-3.5" />
                  Priority
                </span>
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {['low', 'medium', 'high', 'urgent'].map((level) => {
                  const active = formData.priority_level === level;
                  return (
                    <button
                      key={level}
                      type="button"
                      onClick={() =>
                        setFormData({ ...formData, priority_level: level })
                      }
                      className={`min-h-[44px] px-3 py-2 rounded-lg border text-xs font-medium capitalize transition ${
                        active
                          ? priorityPillClass(level)
                          : 'bg-white dark:bg-secondary-800 text-secondary-600 dark:text-secondary-300 border-secondary-200 dark:border-secondary-700 hover:border-secondary-300 dark:hover:border-secondary-600'
                      }`}
                    >
                      {level}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  Due Date <span className="text-red-500">*</span>
                </span>
              </label>
              <input
                type="date"
                className="input-field"
                value={formData.due_date}
                onChange={(e) =>
                  setFormData({ ...formData, due_date: e.target.value })
                }
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">
                Status
              </label>
              <select
                className="input-field"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                {taskStatuses.map((status) => (
                  <option key={status.id} value={status.name}>
                    {status.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {formData.project_id && sprints.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">
                Sprint
              </label>
              <select
                className="input-field"
                value={formData.sprint_id || ''}
                onChange={(e) =>
                  setFormData({ ...formData, sprint_id: e.target.value })
                }
              >
                <option value="">None</option>
                {sprints.map((sprint) => (
                  <option key={sprint.id} value={sprint.id}>
                    {sprint.name}
                    {sprint.start_date &&
                      sprint.end_date &&
                      ` (${String(sprint.start_date).slice(0, 10)} – ${String(
                        sprint.end_date
                      ).slice(0, 10)})`}
                  </option>
                ))}
              </select>
            </div>
          )}
        </FormSection>

        <FormSection
          title="Tags"
          description="Add keywords to make this task easier to find later."
        >
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="relative flex-1">
                <TagIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
                <input
                  type="text"
                  className="input-field pl-9"
                  placeholder="Add a tag and press Enter"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                />
              </div>
              <button
                type="button"
                onClick={handleAddTag}
                className="btn-secondary whitespace-nowrap"
              >
                Add
              </button>
            </div>
            {Array.isArray(formData.tags) && formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={`${tag}-${index}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-200 text-xs font-medium rounded-full border border-primary-200 dark:border-primary-800"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="text-primary-500 hover:text-primary-800 dark:hover:text-primary-100"
                      aria-label={`Remove ${tag}`}
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </FormSection>

        {canManageAssignment && (
          <FormSection
            title="Assignment"
            description="Who is working on this task?"
          >
            <div>
              <label className="flex items-center justify-between gap-3 p-3 rounded-lg border border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800 cursor-pointer hover:border-primary-400 transition-colors">
                <div>
                  <div className="text-sm font-medium text-secondary-700 dark:text-secondary-200">
                    Manual Logging
                  </div>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">
                    {formData.enable_manual_logging === true
                      ? 'Members can manually log time for this task.'
                      : 'Only timer-based logging is allowed (system default).'}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.enable_manual_logging === true}
                  onClick={() =>
                    setFormData({
                      ...formData,
                      enable_manual_logging:
                        formData.enable_manual_logging === true ? null : true,
                    })
                  }
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-secondary-900 ${
                    formData.enable_manual_logging === true
                      ? 'bg-primary-600'
                      : 'bg-secondary-300 dark:bg-secondary-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      formData.enable_manual_logging === true
                        ? 'translate-x-6'
                        : 'translate-x-1'
                    }`}
                  />
                </button>
              </label>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                  <span className="inline-flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5" />
                    Assign To <span className="text-red-500">*</span>
                  </span>
                </label>
                <span
                  className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    formData.assigned_to.length > 0
                      ? 'bg-violet-50 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200'
                      : 'bg-secondary-100 text-secondary-600 dark:bg-secondary-800 dark:text-secondary-400'
                  }`}
                >
                  {formData.assigned_to.length} selected
                </span>
              </div>

              {formData.assigned_to.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {formData.assigned_to.map((userId) => {
                    const member =
                      assigneeSource.find((m) => m.id === userId) ||
                      availableMembers.find((m) => m.id === userId);
                    if (!member) return null;
                    return (
                      <span
                        key={userId}
                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-200 text-xs font-medium rounded-full border border-violet-200 dark:border-violet-800"
                      >
                        {member.full_name || member.username}
                        {member.id === currentUser?.id && ' (You)'}
                        <button
                          type="button"
                          onClick={() => handleMemberToggle(userId)}
                          className="text-violet-500 hover:text-violet-800 dark:hover:text-violet-100"
                          aria-label={`Remove ${member.full_name || member.username}`}
                        >
                          <XIcon className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}

              <select
                className="input-field"
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    handleMemberToggle(e.target.value);
                    e.target.value = '';
                  }
                }}
              >
                <option value="">Add a member…</option>
                {assignableMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.full_name || member.username}
                    {member.id === currentUser?.id && ' (You)'}
                  </option>
                ))}
              </select>

              {formData.assigned_to.length === 0 && (
                <p className="text-xs text-red-500 mt-1.5">
                  At least one team member must be assigned.
                </p>
              )}
              {formData.project_id && projectMembers.length > 0 && (
                <p className="text-xs text-primary-600 dark:text-primary-300 mt-1.5">
                  Showing project members only.
                </p>
              )}
              {formData.project_id &&
                projectMembers.length === 0 &&
                (currentUser?.role === 'admin' ||
                  currentUser?.role === 'project_lead') && (
                  <p className="text-xs text-orange-600 dark:text-orange-300 mt-1.5">
                    No project members found, showing all team members.
                  </p>
                )}
            </div>
          </FormSection>
        )}
      </form>
    </FormDrawer>
  );
};

export default TaskFormDrawer;
